import type { StpChartOption, StpChartValueSemantic, StpChartValueSemantics } from '../types'
import type { StpChartTokens } from '../tokens'
import { applyStpBasePolicy, formatStpSemanticValue } from './base'
import type { OptionItem } from './option-collection'
import { restoreOptionCollection, toOptionArray } from './option-collection'

export interface StpLinePolicyContext {
  tokens: StpChartTokens
  valueSemantic: StpChartValueSemantics
  reducedMotion: boolean
}

type StpXAxisOption = OptionItem<NonNullable<StpChartOption['xAxis']>>
type StpYAxisOption = OptionItem<NonNullable<StpChartOption['yAxis']>>
type StpSeriesOption = OptionItem<NonNullable<StpChartOption['series']>>

function applyAxisPolicy(
  axis: StpXAxisOption,
  context: StpLinePolicyContext,
  semantic: StpChartValueSemantic | undefined,
): StpXAxisOption
function applyAxisPolicy(
  axis: StpYAxisOption,
  context: StpLinePolicyContext,
  semantic: StpChartValueSemantic | undefined,
): StpYAxisOption
function applyAxisPolicy(
  axis: StpXAxisOption | StpYAxisOption,
  context: StpLinePolicyContext,
  semantic: StpChartValueSemantic | undefined,
): StpXAxisOption | StpYAxisOption {
  const axisLabel = {
    ...axis.axisLabel,
    color: context.tokens.mutedColor,
    fontFamily: context.tokens.fontFamily,
    fontSize: context.tokens.fontSize,
    ...(semantic
      ? { formatter: (value: unknown) => formatStpSemanticValue(value, semantic, true) }
      : {}),
  }

  return {
    ...axis,
    axisLabel,
    ...(semantic
      ? {
          splitLine: {
            ...axis.splitLine,
            lineStyle: {
              ...axis.splitLine?.lineStyle,
              color: context.tokens.splitLineColor,
              type: 'dashed',
            },
          },
        }
      : {}),
  }
}

function resolveAxisSemantics(yAxes: StpYAxisOption[], valueSemantic: StpChartValueSemantics) {
  if (!Array.isArray(valueSemantic))
    return yAxes.map(() => valueSemantic)

  if (valueSemantic.length !== yAxes.length)
    throw new Error(`STP line chart value semantics must match yAxis count (${yAxes.length}).`)

  return valueSemantic
}

function applyLineSeriesPolicy(series: StpSeriesOption, tokens: StpChartTokens): StpSeriesOption {
  if (series.type !== 'line')
    return series

  if (typeof series.id !== 'string' || !series.id)
    throw new Error('STP line chart series require a stable id.')

  return {
    ...series,
    smooth: series.smooth ?? true,
    showSymbol: false,
    connectNulls: false,
    lineStyle: {
      ...series.lineStyle,
      width: tokens.lineWidth,
    },
    emphasis: {
      ...series.emphasis,
      lineStyle: {
        ...series.emphasis?.lineStyle,
        width: series.emphasis?.lineStyle?.width ?? 'bolder',
      },
    },
  }
}

/** Applies shared semantic-line presentation without mutating the source option. */
export function applyStpLinePolicy(source: StpChartOption, context: StpLinePolicyContext): StpChartOption {
  const baseOption = applyStpBasePolicy(source, context)
  const xAxis = toOptionArray<StpXAxisOption>(baseOption.xAxis).map(axis => applyAxisPolicy(axis, context, undefined))
  const originalYAxes = toOptionArray<StpYAxisOption>(baseOption.yAxis)
  const axisSemantics = resolveAxisSemantics(originalYAxes, context.valueSemantic)
  const yAxis = originalYAxes.map((axis, index) => applyAxisPolicy(axis, context, axisSemantics[index]))
  const sourceSeries = toOptionArray<StpSeriesOption>(baseOption.series)
  const series = sourceSeries.map(item => applyLineSeriesPolicy(item, context.tokens))

  return {
    ...baseOption,
    ...(xAxis.length ? { xAxis: restoreOptionCollection(xAxis, baseOption.xAxis) } : {}),
    ...(yAxis.length ? { yAxis: restoreOptionCollection(yAxis, baseOption.yAxis) } : {}),
    ...(series.length ? { series } : {}),
  }
}
